export type ComparisonValue =
  | { readonly kind: 'percent'; readonly deltaPercent: number }
  | { readonly kind: 'new' }
  | { readonly kind: 'flat_zero' }
  | { readonly kind: 'not_available' };

export interface RankableProduct {
  readonly productId: string;
  readonly value: number;
}

export interface RankedProduct extends RankableProduct {
  readonly rank: number;
}

export interface IncompleteOperatingProgressAlignment {
  readonly currentBusinessDates: readonly string[];
  readonly baselineBusinessDates: readonly string[];
  readonly currentCutoffAt: string;
  readonly baselineCutoffAt: string;
}

const assertFinite = (value: number, code: string): void => {
  if (!Number.isFinite(value)) throw new Error(code);
};

const assertBusinessDate = (value: string): string => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error('REPORTING_BUSINESS_DATE_INVALID');
  const parsed = new Date(`${value}T00:00:00.000Z`);
  if (!Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value) {
    throw new Error('REPORTING_BUSINESS_DATE_INVALID');
  }
  return value;
};

const normalizedBusinessDates = (values: readonly string[]): string[] =>
  [...new Set(values.map(assertBusinessDate))].sort();

export function compareMetric(current: number, baseline: number | null): ComparisonValue {
  assertFinite(current, 'REPORTING_COMPARISON_CURRENT_INVALID');
  if (baseline === null) return { kind: 'not_available' };
  assertFinite(baseline, 'REPORTING_COMPARISON_BASELINE_INVALID');
  if (baseline === 0) {
    if (current === 0) return { kind: 'flat_zero' };
    if (current > 0) return { kind: 'new' };
    return { kind: 'not_available' };
  }
  return { kind: 'percent', deltaPercent: ((current - baseline) / Math.abs(baseline)) * 100 };
}

export function rankProducts(rows: readonly RankableProduct[]): readonly RankedProduct[] {
  const sorted = rows.map((row) => ({ ...row })).sort((left, right) => {
    assertFinite(left.value, 'REPORTING_RANK_VALUE_INVALID');
    assertFinite(right.value, 'REPORTING_RANK_VALUE_INVALID');
    return right.value - left.value || left.productId.localeCompare(right.productId);
  });
  let previousValue: number | null = null;
  let previousRank = 0;
  return sorted.map((row, index) => {
    const rank = previousValue !== null && row.value === previousValue ? previousRank : index + 1;
    previousValue = row.value;
    previousRank = rank;
    return { ...row, rank };
  });
}

export function topWithBoundaryTies(
  rows: readonly RankedProduct[],
  topN: number,
): readonly RankedProduct[] {
  if (!Number.isSafeInteger(topN) || topN <= 0) throw new Error('REPORTING_TOP_N_INVALID');
  return rows.filter((row) => row.rank <= topN).map((row) => ({ ...row }));
}

export function shiftBusinessDate(businessDate: string, days: number): string {
  assertBusinessDate(businessDate);
  if (!Number.isSafeInteger(days)) throw new Error('REPORTING_BUSINESS_DATE_SHIFT_INVALID');
  const parsed = new Date(`${businessDate}T00:00:00.000Z`);
  parsed.setUTCDate(parsed.getUTCDate() + days);
  return parsed.toISOString().slice(0, 10);
}

export function alignCutoffToBusinessDate(cutoffAt: string, businessDate: string): string {
  assertBusinessDate(businessDate);
  if (!Number.isFinite(Date.parse(cutoffAt))) throw new Error('REPORTING_CUTOFF_INVALID');
  const suffix = cutoffAt.match(/T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})$/)?.[0];
  if (!suffix) throw new Error('REPORTING_CUTOFF_STORE_LOCAL_OFFSET_REQUIRED');
  const aligned = `${businessDate}${suffix}`;
  if (!Number.isFinite(Date.parse(aligned))) throw new Error('REPORTING_CUTOFF_INVALID');
  return aligned;
}

export function alignIncompleteOperatingPeriodProgress(
  currentBusinessDates: readonly string[],
  baselineBusinessDates: readonly string[],
  currentCutoffAt: string,
): IncompleteOperatingProgressAlignment | null {
  const current = normalizedBusinessDates(currentBusinessDates);
  const baseline = normalizedBusinessDates(baselineBusinessDates);
  if (current.length === 0) throw new Error('REPORTING_CURRENT_PERIOD_DATES_REQUIRED');
  const currentLast = current.at(-1)!;
  if (!currentCutoffAt.startsWith(`${currentLast}T`)) {
    throw new Error('REPORTING_CURRENT_CUTOFF_DATE_MISMATCH');
  }
  alignCutoffToBusinessDate(currentCutoffAt, currentLast);
  if (baseline.length < current.length) return null;
  const selectedBaseline = baseline.slice(0, current.length);
  const baselineLast = selectedBaseline.at(-1)!;
  return {
    currentBusinessDates: current,
    baselineBusinessDates: selectedBaseline,
    currentCutoffAt,
    baselineCutoffAt: alignCutoffToBusinessDate(currentCutoffAt, baselineLast),
  };
}

export function previousOperatingBusinessDate(
  currentBusinessDate: string,
  operatingBusinessDates: readonly string[],
): string | null {
  assertBusinessDate(currentBusinessDate);
  const previous = normalizedBusinessDates(operatingBusinessDates)
    .filter((date) => date < currentBusinessDate)
    .at(-1);
  return previous ?? null;
}

export function selectFactsThroughCutoff<T extends {
  readonly businessDate: string;
  readonly occurredAt: string;
}>(
  facts: readonly T[],
  businessDate: string,
  cutoffAt: string,
): readonly T[] {
  assertBusinessDate(businessDate);
  const cutoffMs = Date.parse(cutoffAt);
  if (!Number.isFinite(cutoffMs)) throw new Error('REPORTING_CUTOFF_INVALID');
  return facts.filter((fact) => {
    if (fact.businessDate !== businessDate) return false;
    const occurredMs = Date.parse(fact.occurredAt);
    if (!Number.isFinite(occurredMs)) throw new Error('REPORTING_FACT_TIME_INVALID');
    return occurredMs <= cutoffMs;
  });
}
